<template>
	<BaseModal :title="t('title')" v-model="model">
		<div class="px-5">
			<FairCard @click="openFairInfo" />
			<BaseButton
				class="mt-4 w-full"
				size="lg"
				color="primary"
				@click="copy()"
				:loading="copying"
			>
				{{ t('copy') }}
			</BaseButton>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('data') }}
			</div>
			<BaseCard
				alt
				class="mt-1.5 overflow-x-scroll overflow-y-hidden px-3 py-2 !select-text"
				:class="{ 'max-h-36': !isExpanded }"
			>
				<pre><code v-html="JSON.stringify({ uuid, fair, rounds, cases, tieBreakWinner, tieBreakCandidates }, null, 2)" /></pre>
			</BaseCard>
			<div
				class="text-label-tertiary mt-4 flex w-fit items-center gap-1.5 text-sm font-medium uppercase"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
				@click="toggleExpand()"
			>
				<CaretRightIcon
					class="h-5 w-5 transition-transform"
					:class="{ 'rotate-90': !isExpanded, 'rotate-270': isExpanded }"
				/>
				<div>{{ expandText }}</div>
			</div>
			<div class="bg-fill-secondary mt-4 h-px w-full" />
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('id') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ uuid }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('serverSeed') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ fair.serverSeed ? fair.serverSeed : t('hidden') }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('serverSeedHash') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ fair.serverSeedHash }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('tonBlockNumber') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{
					fair.tonBlockNumber ? fair.tonBlockNumber : t('waiting')
				}}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('tonBlockHash') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ fair.tonBlockHash ? fair.tonBlockHash : 'N/A' }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('tieBreakWinner') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ tieBreakWinner !== null ? tieBreakWinner : 'N/A' }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('tieBreakCandidates') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{
					tieBreakCandidates ? tieBreakCandidates.join(', ') : 'N/A'
				}}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('teamWinner') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ teamIndexWinner !== null ? teamIndexWinner : 'N/A' }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('gamemode') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ gamemode }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('modifiers') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ modifiers }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('roundTickets') }}
			</div>
			<BaseCard
				alt
				class="mt-1.5 px-3 py-2 break-words !select-text"
				v-if="!rounds.length"
			>
				<code>{{ t('waiting') }}</code>
			</BaseCard>
			<BaseCard
				alt
				class="mt-1.5 overflow-x-scroll overflow-y-hidden px-3 py-2 !select-text"
				:class="{ 'max-h-36': !isRoundsExpanded }"
				v-else
			>
				<code v-for="(round, i) in roundTickets">
					<template v-if="i !== 0">---------<br /></template>
					#{{ i + 1 }}:<br />
					{{ round }}<br />
				</code>
			</BaseCard>
			<div
				class="text-label-tertiary mt-4 flex w-fit items-center gap-1.5 text-sm font-medium uppercase"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
				@click="toggleExpandRounds()"
				v-if="rounds.length > 2"
			>
				<CaretRightIcon
					class="h-5 w-5 transition-transform"
					:class="{
						'rotate-90': !isRoundsExpanded,
						'rotate-270': isRoundsExpanded
					}"
				/>
				<div>{{ expandRoundsText }}</div>
			</div>
		</div>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import FairCard from '../FairCard.vue'
import BaseCard from '../BaseCard.vue'
import CaretRightIcon from '@/assets/icons/caret-right.svg'
import { useToast } from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import { useLayout } from '@/stores/layout'

const { t } = useI18n({
	messages: {
		en: {
			title: 'fairness',
			copied: 'Copied to clipboard',
			more: 'show more',
			less: 'show less',
			copy: 'Copy data',
			data: 'Battle data',
			id: 'Battle ID',
			serverSeed: 'Server Seed',
			serverSeedHash: 'Server Seed Hash',
			tonBlockNumber: 'Ton Block Number',
			tonBlockHash: 'Ton Block Hash',
			waiting: 'Waiting for start...',
			hidden: 'Hidden',
			tieBreakWinner: 'Tie-break Winner',
			roundTickets: 'Round Tickets',
			tieBreakCandidates: 'Tie-break Candidates',
			teamWinner: 'Winning Team Index',
			gamemode: 'Gamemode',
			modifiers: 'Modifiers (crazy, fast)'
		},
		ru: {
			title: 'честность',
			copied: 'Скопировано',
			more: 'больше',
			less: 'меньше',
			copy: 'Копировать данные',
			data: 'Данные батла',
			id: 'ID батла',
			serverSeed: 'Сервер-seed',
			serverSeedHash: 'Хеш сервер-seed',
			tonBlockNumber: 'Номер блока Ton',
			tonBlockHash: 'Хеш блока Ton',
			waiting: 'Ожидание начала...',
			hidden: 'Скрыт',
			tieBreakWinner: 'Победитель тай-брейка',
			roundTickets: 'Билеты раундов',
			tieBreakCandidates: 'Кандидаты тай-брейка',
			teamWinner: 'Победившая команда (начиная с 0)',
			gamemode: 'Режим игры',
			modifiers: 'Модификаторы (crazy, fast)'
		}
	}
})

const props = defineProps<{
	cases: {
		name: string
		items: {
			modelUuid: string
			ticketsRange: [number, number]
			chance: number
		}[]
	}[]
	rounds: {
		results: {
			participant: {
				teamIndex: number
				slotIndex: number
				name: string | null
			}
			item: {
				ticket: number
			}
		}[]
	}[]
	fair: {
		serverSeed: string | null
		serverSeedHash: string
		tonBlockHash: string | null
		tonBlockNumber: number | null
	}
	tieBreakWinner: number | null
	teamIndexWinner: number | null
	tieBreakCandidates: number[] | null
	gamemode: 'classic' | 'shared' | 'jackpot'
	modifiers: ('crazy' | 'fast')[]
	uuid: string
}>()

const model = defineModel<boolean>()

const isExpanded = ref(false)

const toggleExpand = () => {
	isExpanded.value = !isExpanded.value
}

const expandText = computed(() => {
	return isExpanded.value ? t('less') : t('more')
})

const isRoundsExpanded = ref(false)

const toggleExpandRounds = () => {
	isRoundsExpanded.value = !isRoundsExpanded.value
}

const expandRoundsText = computed(() => {
	return isRoundsExpanded.value ? t('less') : t('more')
})

const copying = ref(false)

const toast = useToast()

const copy = async () => {
	copying.value = true

	try {
		await navigator.clipboard.writeText(
			JSON.stringify(
				{
					uuid: props.uuid,
					fair: props.fair,
					rounds: props.rounds,
					cases: props.cases,
					tieBreakWinner: props.tieBreakWinner || null,
					tieBreakCandidates: props.tieBreakCandidates || null,
					teamIndexWinner: props.teamIndexWinner || null,
					gamemode: props.gamemode,
					modifiers: props.modifiers
				},
				null,
				2
			)
		)

		toast.add({
			type: 'success',
			text: t('copied')
		})
	} catch {}

	copying.value = false
}

const roundTickets = computed(() => {
	const rounds: string[] = []

	props.rounds.forEach((round) => {
		const group: number[] = []

		round.results.forEach((result) => {
			group.push(result.item.ticket)
		})

		rounds.push(group.join(', '))
	})

	return rounds
})

const layout = useLayout()

const openFairInfo = () => {
	model.value = false
	layout.openFairInfoModal(0)
}
</script>
